import { _js } from '@ifixit/utils/localize';
import { Button, ButtonStyles } from '@ifixit/ui/components/button';
import { FieldLabel } from '@ifixit/ui/components/field';
import { Textarea } from '@ifixit/ui/components/textarea';
import { cn } from '@ifixit/ui/lib/utils';
import type { ReactElement, ReactNode } from 'react';
import { useId } from 'react';
import { Initializer } from 'Shared/chakra-initialize';
import type { WikitextEditor } from 'Shared/editor/use_wikitext_editor';
import type { EditorPreset } from 'Shared/prosemirror/tiptap/presets';
import { GuideIntroMenuBarDisabled } from 'Shared/prosemirror/tiptap/presets/guide_intro_disabled';
import { WikiMenuBarDisabled } from 'Shared/prosemirror/tiptap/presets/wiki_disabled';
import { TiptapEditor } from 'Shared/prosemirror/tiptap/tiptap-lazy';

const VisualEditor = Initializer(TiptapEditor);

export type { WikitextEditor };

// The static toolbar shown in source mode, where no live editor exists to
// drive the real menubar. Presets without a twin fall back to a bare toggle
// bar so the way back to the visual editor stays visible.
const SOURCE_MODE_MENUBAR: Partial<
   Record<EditorPreset, (props: { menubarEnd?: ReactNode }) => ReactElement>
> = {
   guide_intro: GuideIntroMenuBarDisabled,
   wiki: WikiMenuBarDisabled,
};

/**
 * The shared wikitext editor field for the v2 guide and wiki editors: a
 * labeled Tiptap editor whose source/visual toggle rides inside the toolbar,
 * over a bordered content box that shares the toolbar's 8px radius.
 * `?plaintext` (and any unparseable document) starts in source mode and never
 * mounts Tiptap, so it can't crash the page.
 */
export function WikitextField({
   label,
   hint,
   editor,
   preset,
   submitHandler,
   placeholder,
}: {
   label: string;
   hint?: string;
   editor: WikitextEditor;
   preset: EditorPreset;
   /** Fires the editor's keyboard submit (wiki editor); omitted in the guide. */
   submitHandler?: () => void;
   /** Placeholder for the empty source textarea. */
   placeholder?: string;
}) {
   // Shown at every width, which is a change for the guide editor: its old
   // toggle was max-md:hidden because it crowded the phone toolbar. The
   // menubar now pins this outside the scrolling button row, so it costs the
   // buttons nothing — and the wiki editor, whose body is the wikitext-heavy
   // one, always offered source mode on phones.
   const modeToggle = editor.canToggleMode ? (
      <Button
         type="button"
         className={cn(
            ButtonStyles({ variant: 'white' }),
            'h-7 px-3 text-sm md:h-7 disabled:opacity-50'
         )}
         disabled={editor.isSwitchingMode}
         onClick={editor.toggleMode}
      >
         {editor.mode === 'visual' ? _js('Edit source') : _js('Visual editor')}
      </Button>
   ) : null;

   const labelId = useId();
   const SourceMenuBar = SOURCE_MODE_MENUBAR[preset];

   return (
      <div>
         <FieldLabel
            id={labelId}
            htmlFor={editor.mode === 'source' ? editor.editorid : undefined}
            // leading-5 restores text-sm's line-height, which FieldLabel's
            // underlying Label would otherwise collapse with leading-none.
            className="mb-1 block leading-5 text-gray-950"
         >
            {label}
         </FieldLabel>

         {editor.mode === 'visual' ? (
            // The Tiptap content area ships unstyled; the bordered rounded-lg
            // box matches the toolbar's radius and the sibling inputs.
            <div
               aria-labelledby={labelId}
               className="[&_.ProseMirror]:mt-1 [&_.ProseMirror]:min-h-48 [&_.ProseMirror]:!rounded-lg [&_.ProseMirror]:border [&_.ProseMirror]:border-gray-300 [&_.ProseMirror]:bg-white [&_.ProseMirror]:px-3 [&_.ProseMirror]:py-2 [&_.ProseMirror]:outline-none [&_.ProseMirror:focus]:border-ring [&_.ProseMirror:focus]:ring-[3px] [&_.ProseMirror:focus]:ring-ring/50"
            >
               <VisualEditor
                  key={editor.visualDocKey}
                  editorid={editor.editorid}
                  preset={preset}
                  text={editor.visualDoc}
                  submitHandler={submitHandler}
                  handleModified={editor.onModified}
                  menubarEnd={modeToggle}
                  ariaLabelledBy={labelId}
               />
            </div>
         ) : (
            <>
               {/* Source mode has no live toolbar; show the preset's static
                   twin (or a bare toggle) so the chrome and the way back stay
                   in place instead of jumping between modes. The textarea's
                   mt-1 alone spaces it from the bar — matching the visual
                   editor's toolbar-to-content gap. */}
               {modeToggle &&
                  (SourceMenuBar ? (
                     <SourceMenuBar menubarEnd={modeToggle} />
                  ) : (
                     <div className="flex justify-end">{modeToggle}</div>
                  ))}
               <Textarea
                  id={editor.editorid}
                  // field-sizing-content: grow with the text instead of
                  // scrolling a fixed box. The ! overrides beat forms.less's
                  // unlayered bare `textarea` rule (darker border, tighter
                  // radius and padding, its own top margin) so source mode
                  // matches the visual editor's content box.
                  className="field-sizing-content min-h-48 w-full font-mono text-sm !mt-1 !rounded-lg !border-input !px-3 !py-2"
                  placeholder={placeholder}
                  value={editor.sourceText}
                  onChange={event => editor.setSourceText(event.target.value)}
               />
            </>
         )}

         {editor.modeSwitchError && (
            <p className="mt-1 text-sm text-red-600">{editor.modeSwitchError}</p>
         )}
         {hint && <span className="mt-1 block text-xs text-gray-500">{hint}</span>}
      </div>
   );
}
